import { Fragment } from 'react';
import { connect } from 'react-redux';
import {
  ApplicationState,
  CaseSettingsState,
  AppState,
  CaseSettingsActionCreators,
  VehicleState
} from '../../store';
import { withSnackbar, ProviderContext } from 'notistack';
import { AppContext, ApplicationContext } from '../../context/Contexts';
import { ITranslatorService } from '../../services/Interfaces/ITranslatorService';
import { RouteComponentProps } from 'react-router';
import { ScaleLoader } from 'react-spinners';
import { WorkflowSectionStepForm } from '../../interfaces/Workflow';
import { isNullOrUndefined } from 'util';
import { ICaseService } from '../../services/Interfaces/ICaseService';
import { FormHelpers } from '../../helpers/forms/FormHelpers';
import 'react-dropdown-tree-select/dist/styles.css';
import { Box, Button, CircularProgress, Grid, Typography } from '@material-ui/core';
import { AppUser } from '../../interfaces/AppUser';
import { IReferentialService } from '../../services/Interfaces/IReferentialService';
import React from 'react';
import {
  ChangeCaseStatusBasedCodeRequest,
  ChangeCaseStatusRequest,
  OrderSummary,
  OrderSummaryPart
} from '../../interfaces/Case';
import {
  CaseStatusCodeEnum,
  oilCupiCodes,
  OrderPartStatus,
  OrderStatusCode,
  ParametreCode,
  ReferentialCode,
  SupplierCode
} from '../../helpers/Constants';
import { VehicleHelper } from '../../helpers/VehicleHelper';
import { IOrganizationService } from '../../services/Interfaces/IOrganizationService';
import { Organization } from '../../interfaces/Organization';
import { IOrderService } from '../../services/Interfaces/IOrderService';
import { OrderStatusChangeRequestDto, OrderSummaryOrderDto } from '../../interfaces/Order';
import Referentials from '../../helpers/Referentials.json';
import { IReferential } from '../../interfaces/IReferential';
import ArrowBackTwoToneIcon from '@material-ui/icons/ArrowBackTwoTone';
import { Utils } from '../../helpers/Utils';
import MuiTheme from '../../theme';

interface ICaseOrderSummaryFormState {
  isLoading: boolean;
  hasRights: boolean;
  executing: boolean;
  orderSummary: OrderSummary | null;
  partsLocationOrganization: Organization | null;
  parts: OrderSummaryPart[];
  suppliers: Organization[];
  orderStatuses: IReferential[];
  orderPartStatuses: IReferential[];
  isContinueLoading: boolean;
  TVA: string;
}

export interface ExternalCaseDetailsCarInServiceFormProps {
  workflowForm: WorkflowSectionStepForm;
}

type ICaseOrderSummaryFormProps = ExternalCaseDetailsCarInServiceFormProps & {
  caseSettingsState: CaseSettingsState;
  appState: AppState;
  vehicleState: VehicleState;
} & typeof CaseSettingsActionCreators &
  ProviderContext &
  RouteComponentProps<{ id: string }>;

class CaseOrderSummaryForm extends React.PureComponent<
  ICaseOrderSummaryFormProps,
  ICaseOrderSummaryFormState
> {
  private translatorService!: ITranslatorService;
  private caseService!: ICaseService;
  private appReferentialService!: IReferentialService;
  private organizationService!: IOrganizationService;
  private orderService!: IOrderService;

  static contextType = ApplicationContext;
  state = {
    isLoading: false,
    hasRights: false,
    isUserHidden: false,
    executing: false,
    orderSummary: null,
    parts: [],
    suppliers: [],
    partsLocationOrganization: null,
    orderStatuses: [],
    orderPartStatuses: [],
    isContinueLoading: false,
    TVA: '0'
  } as ICaseOrderSummaryFormState;

  public componentDidMount() {
    const caseSettings = this.props.caseSettingsState.caseSettings;
    const caseId = Number.parseInt(this.props.match.params.id);
    if (isNullOrUndefined(caseSettings) || Number.isNaN(caseId)) {
      return;
    }

    this.setState(
      {
        isLoading: true
      },
      async () => {
        await this.loadData();
      }
    );
  }

  loadData = async () => {
    const caseId = Number.parseInt(this.props.match.params.id);
    if (Number.isNaN(caseId)) {
      return;
    }
    const refOrderStatus = Referentials.referential.find(
      (item) => item.code === ReferentialCode.OrderStatus
    );
    const refOrderPartStatus = Referentials.referential.find(
      (item) => item.code === ReferentialCode.OrderPartStatus
    );

    const [orderStatuses, orderPartStatuses, orderSummary] = await Promise.all([
      this.appReferentialService.Get(refOrderStatus!.baseUrl),
      this.appReferentialService.Get(refOrderPartStatus!.baseUrl),
      this.caseService.GetOrderSummary(caseId)
    ]);

    if (orderSummary === null) {
      this.setState({ isLoading: false });
      return;
    }

    let partsLocationOrganization = null;
    if (!isNullOrUndefined(orderSummary.partsLocationOrganizationId)) {
      partsLocationOrganization = await this.organizationService.GetOrganization(
        orderSummary.partsLocationOrganizationId!
      );
    }

    let TVA = '0';
    if (orderSummary.amountWithoutTVA !== 0) {
      TVA = ((orderSummary.amountTVA / orderSummary.amountWithoutTVA) * 100).toFixed(0);
    } else {
      const caseParameterHistory = await this.caseService.GetCaseParameterHistory(
        orderSummary.caseId
      );
      const tvaParam = caseParameterHistory.find(
        (item) => item.parameterCode === ParametreCode.TVA
      );
      if (tvaParam) {
        TVA = tvaParam.value;
      }
    }

    const hasRights = FormHelpers.HasRights(
      this.props.workflowForm.workflowFormPermissions,
      this.props.appState.appUser!,
      this.props.appState,
      this.props.caseSettingsState.case!.caseStatus.caseStatusId
    );

    const parts = orderSummary.parts;
    const supplierIds = parts.filter((x) => x.supplierId != null).map((x) => x.supplierId!);
    const suppliers =
      supplierIds.length > 0
        ? await this.organizationService.GetUserOrganizationsByIds(Array.from(new Set(supplierIds)))
        : [];

    parts.forEach((p) => {
      const supplier = suppliers.find((item) => item.id === p.supplierId);
      p.supplier = supplier === undefined ? null : supplier;
    });

    this.setState({
      isLoading: false,
      hasRights: hasRights,
      orderSummary: orderSummary,
      partsLocationOrganization: partsLocationOrganization,
      parts: parts,
      suppliers: suppliers,
      orderPartStatuses: orderPartStatuses,
      orderStatuses: orderStatuses,
      TVA: TVA
    });
  };

  submitForm = async (caseId: number, caseSectionStepFormId: number, appUser: AppUser | null) => {
    let newStatus = await this.caseService.SubmitForm(caseId, caseSectionStepFormId, appUser);

    if (newStatus === null) {
      newStatus = this.props.caseSettingsState.case!.caseStatus;
    }

    this.props.SetCaseStatus(newStatus);
    if (appUser !== null) {
      this.props.AddPartner(appUser!.hoId === null ? appUser!.organizationId : appUser!.hoId);
    }

    const hasRights = FormHelpers.HasRights(
      this.props.workflowForm.workflowFormPermissions,
      this.props.appState.appUser!,
      this.props.appState,
      newStatus.caseStatusId
    );
    this.setState({ hasRights: hasRights });
  };

  submitPlaceOrder = async () => {
    try {
      this.setState({ executing: true, isContinueLoading: true });
      const orders = await this.orderService.CreateOrderSummary({
        caseId: this.props.caseSettingsState.case!.id
      });

      if (orders.length > 0) {
        for (const order of orders) {
          await this.placeOrder(order);
        }
      }

      await this.submitForm(
        this.props.caseSettingsState.case!.id,
        this.props.workflowForm.id,
        null
      );

      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (ex) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    } finally {
      this.setState({ executing: false, isContinueLoading: false });
    }
  };

  placeOrder = async (newOrder: OrderSummaryOrderDto) => {
    const supplier = this.state.suppliers.find((s) => s.id === newOrder.supplierId);
    if (!supplier) {
      return;
    }
    const orderedPartStatus = this.state.orderPartStatuses.find(
      (item) => item.code === OrderPartStatus.PROCESSING
    )!;
    const nextOrderStatus = this.state.orderStatuses.find(
      (item) => item.code === OrderStatusCode.REP_PLASATA
    );

    const request = {
      statusId: nextOrderStatus?.id,
      orderPartStatusId: orderedPartStatus.id,
      comment: '',
      orderId: newOrder.orderId,
      attachments: [],
      partsLocationOrganizationId: this.state.orderSummary?.partsLocationOrganizationId,
      workflowId: this.props.caseSettingsState?.caseSettings?.workflow.id,
      partsOrganizationId: this.props.appState.appUser?.organizationId
    } as OrderStatusChangeRequestDto;

    await this.orderService.ChangeOrderStatus(request);

    if (
      supplier?.name == SupplierCode.MATEROM ||
      supplier?.name == SupplierCode.ADTOTAL ||
      supplier?.name == SupplierCode.CONEX ||
      supplier?.name == SupplierCode.EUROEST ||
      supplier?.name == SupplierCode.LKQ ||
      supplier?.name == SupplierCode.NESTE
    ) {
      const order = await this.orderService.GetOrder(newOrder.orderId, false);
      if (order.supplierOrderNumber) {
        const proceedOrderStatus = this.state.orderStatuses.find(
          (item) => item.code === OrderStatusCode.FZR_COMANDA_CONFIRMATA
        );
        const proceedPartStatus = this.state.orderPartStatuses.find(
          (item) => item.code === OrderPartStatus.PROCESSED
        )!;
        const request = {
          statusId: proceedOrderStatus?.id,
          orderPartStatusId: proceedPartStatus.id,
          comment: '',
          orderId: newOrder.orderId,
          attachments: [],
          partsLocationOrganizationId: this.state.orderSummary?.partsLocationOrganizationId,
          workflowId: this.props.caseSettingsState?.caseSettings?.workflow.id,
          partsOrganizationId: this.props.appState.appUser?.organizationId
        } as OrderStatusChangeRequestDto;

        await this.orderService.ChangeOrderStatus(request);
        await this.caseService.SendConfirmedOrderNotification(
          this.state.orderSummary!.caseId,
          newOrder.orderId
        );
      }
    }
  };

  handleCancel = async () => {
    try {
      this.setState({ executing: true });
      const caseStatusRequest = {
        caseId: this.props.caseSettingsState.case!.id,
        statusId: CaseStatusCodeEnum.Closed
      } as ChangeCaseStatusRequest;

      await this.caseService.ChangeCaseStatus(caseStatusRequest);
      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });

      this.props.history.push('/newCase');
    } catch (ex) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    } finally {
      this.setState({ executing: false });
    }
  };

  handleBack = async () => {
    try {
      this.setState({ executing: true });
      const caseStatusRequest = {
        caseId: this.props.caseSettingsState.case!.id,
        statusCode: CaseStatusCodeEnum.SELECTIE_PIESE
      } as ChangeCaseStatusBasedCodeRequest;
      const newStatus = await this.caseService.ChangeCaseStatusBasedCode(caseStatusRequest);
      this.props.SetCaseStatus(newStatus);
      this.setState({ executing: false });
      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (ex) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    } finally {
      this.setState({ executing: false });
    }
  };

  renderOrderSummaryDetailsSection = () => {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={6}>
              <div className="text-black font-weight-bold mb-2">
                {this.translatorService.Tranlate(
                  'CALCULATION_DETAILS_VEHICAL_PLATE_NUMBER',
                  'Numar inmatriculare'
                )}
              </div>
              <div className="text-black font-weight-bold  mb-2">
                {this.translatorService.Tranlate('CAR_DETAILS_BRAND', 'Marca')}
              </div>
              <div className="text-black font-weight-bold  mb-2">
                {this.translatorService.Tranlate('CAR_DETAILS_BRAND', 'Marca')}
              </div>
              <div className="text-black font-weight-bold  mb-2">
                {this.translatorService.Tranlate(
                  'CASE_ORDER_SUMMARY_KM_NUMBER',
                  'Rulaj actual (km)'
                )}
              </div>
              <div className="text-black font-weight-bold  mb-2">
                {this.translatorService.Tranlate(
                  'CASE_ORDER_SUMMARY_SERVICE_NAME',
                  'Unitate service'
                )}
              </div>
              <div className="text-black font-weight-bold  mb-2">
                {this.translatorService.Tranlate(
                  'CASE_ORDER_SUMMARY_DELIVERY_ADRESS',
                  'Adresa livrare'
                )}
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className="mb-2">
                {VehicleHelper.GetPlateNumber(
                  this.props.caseSettingsState.case,
                  this.props.vehicleState.vehicle
                )}
              </div>
              <div className="mb-2">
                {VehicleHelper.GetBrand(
                  this.props.caseSettingsState.case,
                  this.props.vehicleState.vehicle
                )}
              </div>
              <div className="mb-2">
                {VehicleHelper.GetModel(
                  this.props.caseSettingsState.case,
                  this.props.vehicleState.vehicle
                )}
              </div>
              <div className="mb-2">
                {VehicleHelper.GetKmNr(
                  this.props.caseSettingsState.case,
                  this.props.vehicleState.vehicle
                )}
              </div>
              <div className="mb-2">
                {!isNullOrUndefined(this.state.partsLocationOrganization)
                  ? this.state.partsLocationOrganization.name
                  : ''}
              </div>
              <div className="mb-2">
                {!isNullOrUndefined(this.state.partsLocationOrganization)
                  ? this.state.partsLocationOrganization.address
                  : ''}
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  getPieceNumber = (part: OrderSummaryPart) => {
    if (oilCupiCodes.includes(part.repairMethod)) {
      return part.volumeLiters ? part.volumeLiters * part.pieceNr + 'L' : part.pieceNr;
    }
    return part.pieceNr;
  };

  renderOrderSummaryPartsSection = () => {
    const { parts } = this.state;

    return (
      <Box>
        <Typography variant="h4" gutterBottom className="mt-3 mb-3">
          {this.translatorService.Tranlate('ORDER_SUMMARY_PART_LIST_LABEL', 'Lista piese')}
        </Typography>

        <Grid container spacing={1}>
          {parts.map((part, index) => (
            <Grid item xs={12} key={index}>
              <Box
                p={1}
                boxShadow={1}
                borderRadius={8}
                bgcolor="#fff"
                style={{
                  borderLeft: `4px solid ${MuiTheme.palette.primary.main}`
                }}
              >
                <Typography variant="subtitle1" gutterBottom>
                  <strong>
                    {this.translatorService.Tranlate('ORDER_SUMMARY_NAME_LABEL', 'Denumire piesa')}
                  </strong>
                  :{' '}
                  {!isNullOrUndefined(part!.alternativePartQualityId)
                    ? part.alternativePartName
                    : part.name}
                </Typography>
                <Typography variant="body2">
                  <strong>
                    {this.translatorService.Tranlate('ORDER_SUMMARY_CODE_LABEL', 'Cod')}:
                  </strong>{' '}
                  {!isNullOrUndefined(part!.alternativePartQualityId)
                    ? part.alternativeGuideNumber
                    : part.guideNumber}
                </Typography>
                <Typography variant="body2">
                  <strong>
                    {this.translatorService.Tranlate('ORDER_SUMMARY_PRODUCER_LABEL', 'Producător')}:
                  </strong>{' '}
                  {part.producer}
                </Typography>
                <Typography variant="body2">
                  <strong>
                    {this.translatorService.Tranlate(
                      'ORDER_SUMMARY_DELIVERY_TIME_LABEL',
                      'Timp livrare'
                    )}
                    :
                  </strong>{' '}
                  {part.deliveryTime}
                </Typography>
                <Typography variant="body2">
                  <strong>
                    {this.translatorService.Tranlate('ORDER_SUMMARY_PIECE_NR_LABEL', 'Cant.')}:
                  </strong>{' '}
                  {this.getPieceNumber(part)}
                </Typography>
                <Typography variant="body2">
                  <strong>
                    {this.translatorService.Tranlate('ORDER_SUMMARY_TOTAL_PRICE_LABEL', 'Preț')}:
                  </strong>{' '}
                  {part.totalPrice} {this.state.orderSummary?.currencyCode}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  renderOrderSummaryTotalPrice = () => {
    return (
      <>
        <Typography variant="h4" gutterBottom className="mt-3 mb-3">
          {this.translatorService.Tranlate('ORDER_SUMMARY_TOTAL_ORDER_TITLE', 'Total Comanda')}
        </Typography>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            fontSize: '16px'
          }}
        >
          <div style={{ textAlign: 'left', width: 'fit-content' }}>
            <span className="font-weight-bold d-block">
              {this.translatorService.Tranlate(
                'ORDER_SUMMARY_TOTAL_NO_TVA_LABEL',
                'Total fara TVA'
              )}
              :{' '}
              {this.state.orderSummary
                ? Utils.countryNumberFormat(
                    this.state.orderSummary.amountWithoutTVA.toFixed(2),
                    this.props.appState.appUser?.organization?.country ?? null
                  )
                : null}{' '}
              {this.state.orderSummary?.currencyCode}
            </span>
            <span className="font-weight-bold d-block">
              {this.translatorService.Tranlate('ORDER_SUMMARY_TVA_LABEL', 'TVA')}:{' '}
              {this.state.TVA !== '' && !isNullOrUndefined(this.state.TVA)
                ? ' (' + this.state.TVA + '%)'
                : ''}
              :{' '}
              {this.state.orderSummary
                ? Utils.countryNumberFormat(
                    this.state.orderSummary.amountTVA.toFixed(2),
                    this.props.appState.appUser?.organization?.country ?? null
                  )
                : null}{' '}
              {this.state.orderSummary?.currencyCode}
            </span>
            <span className="font-weight-bold d-block">
              {this.translatorService.Tranlate('ORDER_SUMMARY_TOTAL_LABEL', 'Total')}:{' '}
              {this.state.orderSummary
                ? Utils.countryNumberFormat(
                    this.state.orderSummary.amount.toFixed(2),
                    this.props.appState.appUser?.organization?.country ?? null
                  )
                : null}{' '}
              {this.state.orderSummary?.currencyCode}
            </span>
          </div>
        </div>
      </>
    );
  };

  renderBottomButtons = () => {
    return (
      <div
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        className="mt-3 mb-3"
      >
        <Button
          className="m-2"
          variant="contained"
          color="secondary"
          type="submit"
          disabled={!this.state.hasRights || this.state.executing}
          onClick={this.submitPlaceOrder}
        >
          {this.state.isContinueLoading && (
            <CircularProgress size={16} style={{ marginRight: 5 }} />
          )}
          {this.translatorService.Tranlate('PLACE_ORDER_SUMMERY_LABEL', 'Trimite comanda')}
        </Button>

        <Button
          style={{
            background: 'transparent',
            boxShadow: 'none',
            textTransform: 'none',
            padding: 0,
            minWidth: 'auto',
            fontSize: '0.75rem',
            color: 'var(--primary)',
            textDecoration: 'underline'
          }}
          onClick={this.handleCancel}
          className="link-button"
          disabled={!this.state.hasRights || this.state.executing}
        >
          {this.translatorService.Tranlate('CANCEL_BUTTON_LABEL', 'Anulează')}
        </Button>
      </div>
    );
  };

  renderBackButton = () => {
    return (
      <Grid container spacing={0} style={{ marginBottom: '20px' }}>
        <Grid item xs={12}>
          <Button
            className="shadow-none custom-button"
            variant="outlined"
            color="secondary"
            size="small"
            disabled={!this.state.hasRights || this.state.executing}
            startIcon={<ArrowBackTwoToneIcon />}
            onClick={this.handleBack}
          >
            {this.translatorService.Tranlate('CASE_PART_SELECTION_BACK_BUTTON', 'Selectie piese')}
          </Button>
        </Grid>
      </Grid>
    );
  };

  public render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.caseService = (this.context as AppContext).caseService;
    this.appReferentialService = (this.context as AppContext).referentialService;
    this.organizationService = (this.context as AppContext).organizationService;
    this.orderService = (this.context as AppContext).orderService;

    return (
      <Fragment>
        <div className="d-flex flex-row text-center flex-wrap justify-content-center">
          <ScaleLoader color={'var(--primary)'} loading={this.state.isLoading} />
        </div>
        {!this.state.isLoading ? (
          <div className="m-2 ml-4 mr-4">
            {this.renderBackButton()}
            {this.renderOrderSummaryDetailsSection()}
            {this.renderOrderSummaryPartsSection()}
            {this.renderOrderSummaryTotalPrice()}
            {this.renderBottomButtons()}
          </div>
        ) : null}
      </Fragment>
    );
  }
}

const mergeProps = (
  stateProps: any,
  dispatchProps: any,
  ownProps: ExternalCaseDetailsCarInServiceFormProps
) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps
});

export default connect(
  (state: ApplicationState) => ({
    caseSettingsState: state.caseSettings,
    appState: state.app,
    vehicleState: state.vehicle
  }),
  CaseSettingsActionCreators,
  mergeProps
)(withSnackbar(CaseOrderSummaryForm as any));
